import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import type { User, Session } from '@supabase/supabase-js'
import { AdminDashboard } from './components/dashboard/AdminDashboard'
import { LoginPage } from './auth/LoginPage'
import { setSentryUser, clearSentryUser } from './lib/sentry'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Tag Sentry events with whoever is signed in. Without this every
    // admin crash shows up in Sentry with user:null, which is useless
    // for filtering "my session vs someone else's session" or for
    // reaching back out to the operator who hit the bug.
    const applySession = (next: Session | null) => {
      setSession(next)
      setUser(next?.user ?? null)
      setLoading(false)
      if (next?.user) {
        setSentryUser({ id: next.user.id, email: next.user.email })
      } else {
        clearSentryUser()
      }
    }

    supabase.auth.getSession().then(({ data: { session } }) => applySession(session))

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    })
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 dark:bg-stone-950">
        <div className="flex items-center gap-3 text-stone-500">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading...
        </div>
      </div>
    )
  }

  if (!user || !session) {
    return <LoginPage onLogin={signIn} />
  }

  return <AdminDashboard session={session} user={user} onSignOut={signOut} />
}
